import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { usePermissions, daysLeft } from "../lib/usePermissions";
import { PlanNotices } from "../components/PlanNotices";
import { SiteLogo } from "../components/SiteLogo";
import { LangSwitcher } from "../components/LangSwitcher";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Input } from "../components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "../components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  Plus, Layers, Trash2, LogOut, Clock, LayoutTemplate, Trophy, Timer, UserCircle,
  Eye, ShieldCheck, Copy, FolderPlus, Folder, FolderOpen, MoreVertical, FolderInput, Sparkles, ChevronDown, ChevronUp,
} from "lucide-react";
import { TEMPLATES } from "../lib/templates";
import { YouTubeKeyWizard } from "../components/YouTubeKeyWizard";
import { YouTubeIcon, YouTubeBrandIcon } from "../components/YouTubeIcon";
import { t } from "../i18n";

const TPL_ICONS = { lower: LayoutTemplate, score: Trophy, timer: Timer, ytsubs: YouTubeBrandIcon, ytviewers: Eye };

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [ytStatus, setYtStatus] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [ytCardDismissed, setYtCardDismissed] = useState(() => sessionStorage.getItem("yt_card_dismissed") === "1");
  const dismissYtCard = () => { setYtCardDismissed(true); sessionStorage.setItem("yt_card_dismissed", "1"); };
  useEffect(() => {
    api.get("/youtube/status").then(({ data }) => setYtStatus(data)).catch(() => {});
  }, []);
  const navigate = useNavigate();
  const [overlays, setOverlays] = useState([]);
  const { perm } = usePermissions();
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState("all"); // "all" | "none" | folderId
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState(false);
  const [fmt, setFmt] = useState("16:9");
  const [cw, setCw] = useState(1080);
  const [ch, setCh] = useState(1920);
  const [folderOpen, setFolderOpen] = useState(false);
  const [folderName, setFolderName] = useState("");
  // Templates recolhíveis (preferência lembrada neste navegador).
  const [tplCollapsed, setTplCollapsed] = useState(() => { try { return localStorage.getItem("lgfx_templates_collapsed") === "1"; } catch { return false; } });
  const toggleTemplates = () => {
    const v = !tplCollapsed; setTplCollapsed(v);
    try { localStorage.setItem("lgfx_templates_collapsed", v ? "1" : "0"); } catch {}
  };

  const load = async () => {
    try {
      const [ov, fd] = await Promise.all([api.get("/overlays"), api.get("/folders")]);
      setOverlays(ov.data);
      setFolders(fd.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try {
      const P = { "16:9": [1920, 1080], "9:16": [1080, 1920], "1:1": [1080, 1080] };
      const [largura, altura] = fmt === "custom" ? [Number(cw) || 1920, Number(ch) || 1080] : P[fmt];
      const { data } = await api.post("/overlays", { nome: name || t("Novo Overlay"), largura, altura });
      if (activeFolder !== "all" && activeFolder !== "none") {
        await api.put(`/overlays/${data.id}`, { folder_id: activeFolder });
      }
      setOpen(false);
      setName("");
      navigate(`/studio/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Erro ao criar overlay"));
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/overlays/${id}`);
    setOverlays((o) => o.filter((x) => x.id !== id));
    toast.success(t("Overlay excluído"));
  };

  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/overlays/${id}/duplicate`);
      setOverlays((o) => [data, ...o]);
      toast.success(t("Overlay duplicado"));
    } catch {
      toast.error(t("Erro ao duplicar"));
    }
  };

  const moveToFolder = async (ov, folderId) => {
    try {
      await api.put(`/overlays/${ov.id}`, { folder_id: folderId });
      setOverlays((o) => o.map((x) => (x.id === ov.id ? { ...x, folder_id: folderId } : x)));
      toast.success(folderId ? t("Movido para a pasta") : t("Removido da pasta"));
    } catch {
      toast.error(t("Erro ao mover"));
    }
  };

  const createFolder = async () => {
    const nome = folderName.trim();
    if (!nome) return;
    try {
      const { data } = await api.post("/folders", { nome });
      setFolders((f) => [...f, data]);
      setFolderName("");
      setFolderOpen(false);
      setActiveFolder(data.id);
      toast.success(t("Pasta criada"));
    } catch {
      toast.error(t("Erro ao criar pasta"));
    }
  };

  const removeFolder = async (folderId) => {
    try {
      await api.delete(`/folders/${folderId}`);
      setFolders((f) => f.filter((x) => x.id !== folderId));
      setOverlays((o) => o.map((x) => (x.folder_id === folderId ? { ...x, folder_id: null } : x)));
      if (activeFolder === folderId) setActiveFolder("all");
      toast.success(t("Pasta excluída"));
    } catch {
      toast.error(t("Erro ao excluir pasta"));
    }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  const startTemplate = async (tpl) => {
    try {
      const { data } = await api.post("/overlays", { nome: tpl.nome });
      const patch = { elements: tpl.build() };
      if (activeFolder !== "all" && activeFolder !== "none") patch.folder_id = activeFolder;
      await api.put(`/overlays/${data.id}`, patch);
      navigate(`/studio/${data.id}`);
    } catch {
      toast.error(t("Erro ao criar a partir do template"));
    }
  };

  const countFor = (key) =>
    key === "all" ? overlays.length
      : key === "none" ? overlays.filter((o) => !o.folder_id).length
        : overlays.filter((o) => o.folder_id === key).length;

  const visible = overlays.filter((o) =>
    activeFolder === "all" ? true
      : activeFolder === "none" ? !o.folder_id
        : o.folder_id === activeFolder);

  const folderName_ = (id) => folders.find((f) => f.id === id)?.nome;

  const FolderChip = ({ id, label, icon: Icon, deletable }) => (
    <div className="relative group/chip">
      <button
        onClick={() => setActiveFolder(id)}
        className={`flex items-center gap-1.5 rounded-full border px-3 h-8 text-xs whitespace-nowrap transition-colors ${
          activeFolder === id
            ? "border-primary bg-primary/15 text-primary"
            : "border-border/60 bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground"
        }`}
        data-testid={`folder-chip-${id}`}
      >
        <Icon className="h-3.5 w-3.5" />
        {label}
        <span className="opacity-60">{countFor(id)}</span>
      </button>
      {deletable && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-destructive text-white items-center justify-center hidden group-hover/chip:flex"
              data-testid={`folder-delete-${id}`}
              title={t("Excluir pasta")}
            >
              <Trash2 className="h-2.5 w-2.5" />
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("Excluir a pasta \"{nome}\"?", { nome: label })}</AlertDialogTitle>
              <AlertDialogDescription>{t("Os overlays dentro dela não serão apagados — apenas voltam para \"Sem pasta\".")}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("Cancelar")}</AlertDialogCancel>
              <AlertDialogAction onClick={() => removeFolder(id)} data-testid={`confirm-folder-delete-${id}`}>{t("Excluir pasta")}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PlanNotices />
      <header className="h-16 border-b border-border/60 flex items-center justify-between px-6 md:px-10 bg-card">
        <div className="flex items-center" data-testid="brand-logo">
          <SiteLogo className="h-9 w-auto" />
        </div>
        <div className="flex items-center gap-3">
        <LangSwitcher className="hidden sm:inline-flex" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="user-menu-btn">
              {perm && !perm.is_admin && (
                <span onClick={(e) => { e.stopPropagation(); navigate("/planos"); }} className="hidden sm:flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full bg-secondary hover:bg-accent cursor-pointer" data-testid="plan-badge">
                  <span className="font-semibold text-foreground">{perm.plano_nome || t("Sem plano")}</span>
                  {perm.expira_em && <span className={daysLeft(perm.expira_em) <= 7 ? "text-amber-400" : "text-muted-foreground"}>· {daysLeft(perm.expira_em)}d</span>}
                </span>
              )}
              <Avatar className="h-8 w-8">
                <AvatarImage src={user?.avatar_url} />
                <AvatarFallback>{user?.name?.[0] || "U"}</AvatarFallback>
              </Avatar>
              <span className="text-sm hidden sm:block">{user?.name}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {user?.is_admin && (
              <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="admin-link">
                <ShieldCheck className="h-4 w-4 mr-2" /> {t("Painel Admin")}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="profile-link">
              <UserCircle className="h-4 w-4 mr-2" /> {t("Meu Perfil")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/planos")} data-testid="plans-link">
              <Sparkles className="h-4 w-4 mr-2" /> {t("Planos")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={doLogout} data-testid="logout-btn">
              <LogOut className="h-4 w-4 mr-2" /> {t("Sair")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        </div>
      </header>

      {ytStatus && !ytStatus.has_key && !ytCardDismissed && (
        <div className="border-b border-border/60 bg-primary/5 px-6 md:px-10 py-3" data-testid="yt-connect-card">
          <div className="max-w-6xl mx-auto flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <YouTubeIcon className="hidden sm:block" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">{t("Conecte seu YouTube")}</p>
              <p className="text-xs text-muted-foreground">{t("Exiba espectadores, likes e comentários ao vivo nos seus overlays.")}</p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <Button size="sm" className="rounded-sm h-8" onClick={() => setWizardOpen(true)} data-testid="yt-card-connect-btn">{t("Conectar agora")}</Button>
              <Button size="sm" variant="ghost" className="rounded-sm h-8" onClick={dismissYtCard} data-testid="yt-card-dismiss-btn">{t("Agora não")}</Button>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display font-extrabold text-3xl tracking-tight">{t("Meus Overlays")}</h1>
            <p className="text-sm text-muted-foreground mt-1 flex items-center gap-2">
              {t("Crie, edite e coloque no ar.")}
              {ytStatus && (ytStatus.has_key || ytCardDismissed) && (
                ytStatus.has_key ? (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-emerald-500/15 text-emerald-400" data-testid="yt-status-chip">
                    YouTube: {t("conectado")}
                  </span>
                ) : (
                  <button type="button" onClick={() => setWizardOpen(true)}
                    className="text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-500/15 text-amber-400 hover:bg-amber-500/25 underline-offset-2 hover:underline transition-colors"
                    title={t("Configurar a chave do YouTube")} data-testid="yt-status-chip">
                    YouTube: {t("não configurado")} · {t("configurar")}
                  </button>
                )
              )}
            </p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm" data-testid="new-overlay-btn">
                <Plus className="h-4 w-4 mr-2" /> {t("Novo Overlay")}
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="new-overlay-dialog">
              <DialogHeader><DialogTitle className="font-display">{t("Novo Overlay")}</DialogTitle>
                <DialogDescription>{t("Dê um nome ao seu overlay para começar a editar.")}</DialogDescription>
              </DialogHeader>
              <Input
                autoFocus
                placeholder={t("Nome do overlay")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && create()}
                data-testid="overlay-name-input"
              />
              <div className="space-y-2" data-testid="format-presets">
                <p className="text-[10px] font-mono tracking-[0.2em] text-muted-foreground">{t("FORMATO")}</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { k: "16:9", label: t("16:9 · Horizontal"), sub: "1920×1080" },
                    { k: "9:16", label: t("9:16 · Vertical"), sub: "1080×1920" },
                    { k: "1:1", label: t("1:1 · Quadrado"), sub: "1080×1080" },
                    { k: "custom", label: t("Personalizado"), sub: t("livre") },
                  ].map((p) => (
                    <button
                      key={p.k}
                      type="button"
                      onClick={() => setFmt(p.k)}
                      className={`text-left rounded-sm border px-3 py-2 transition-colors ${fmt === p.k ? "border-primary bg-primary/10" : "border-border/60 hover:border-primary/40"}`}
                      data-testid={`format-preset-${p.k}`}
                    >
                      <span className="block text-xs font-semibold">{p.label}</span>
                      <span className="block text-[10px] text-muted-foreground">{p.sub}</span>
                    </button>
                  ))}
                </div>
                {fmt === "custom" && (
                  <div className="flex items-center gap-2">
                    <input type="number" value={cw} onChange={(e) => setCw(e.target.value)} className="w-full h-8 bg-input border border-border rounded-sm px-2 text-xs" placeholder={t("Largura")} data-testid="format-custom-w" />
                    <span className="text-muted-foreground text-xs">×</span>
                    <input type="number" value={ch} onChange={(e) => setCh(e.target.value)} className="w-full h-8 bg-input border border-border rounded-sm px-2 text-xs" placeholder={t("Altura")} data-testid="format-custom-h" />
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button onClick={create} disabled={creating} className="rounded-sm" data-testid="create-overlay-confirm">
                  {creating ? t("Criando...") : t("Criar e editar")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="mb-10" data-testid="templates-section">
          <div className="flex items-center justify-between gap-3 mb-3">
            <p className="text-[10px] font-mono tracking-[0.2em] text-muted-foreground">{t("COMEÇAR COM UM TEMPLATE")}</p>
            <button
              onClick={toggleTemplates}
              aria-expanded={!tplCollapsed}
              className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
              data-testid="templates-toggle"
            >
              {tplCollapsed ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
              {tplCollapsed ? t("Mostrar templates") : t("Recolher")}
            </button>
          </div>
          {!tplCollapsed && <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {TEMPLATES.map((tpl) => {
              const Icon = TPL_ICONS[tpl.key] || LayoutTemplate;
              return (
                <button
                  key={tpl.key}
                  onClick={() => startTemplate(tpl)}
                  className="text-left border border-border/60 bg-card rounded-sm p-4 hover:border-primary/50 hover:bg-secondary/40 transition-colors duration-200 flex items-start gap-3"
                  data-testid={`template-${tpl.key}`}
                >
                  <span className="h-9 w-9 rounded-sm bg-primary/15 text-white flex items-center justify-center flex-shrink-0">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-display font-bold text-sm">{tpl.nome}</span>
                    <span className="block text-xs text-muted-foreground mt-0.5">{tpl.desc}</span>
                  </span>
                </button>
              );
            })}
          </div>}
        </div>

        {/* Pastas */}
        <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="folders-bar">
          <FolderChip id="all" label={t("Todos")} icon={Layers} />
          <FolderChip id="none" label={t("Sem pasta")} icon={Folder} />
          {folders.map((f) => (
            <FolderChip key={f.id} id={f.id} label={f.nome} icon={FolderOpen} deletable />
          ))}
          <Dialog open={folderOpen} onOpenChange={setFolderOpen}>
            <DialogTrigger asChild>
              <button
                className="flex items-center gap-1.5 rounded-full border border-dashed border-border/60 px-3 h-8 text-xs text-muted-foreground hover:border-primary/40 hover:text-foreground transition-colors"
                data-testid="new-folder-btn"
              >
                <FolderPlus className="h-3.5 w-3.5" /> {t("Nova pasta")}
              </button>
            </DialogTrigger>
            <DialogContent data-testid="new-folder-dialog">
              <DialogHeader><DialogTitle className="font-display">{t("Nova pasta")}</DialogTitle>
                <DialogDescription>{t("Organize seus overlays em pastas.")}</DialogDescription>
              </DialogHeader>
              <Input
                autoFocus
                placeholder={t("Nome da pasta")}
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && createFolder()}
                data-testid="folder-name-input"
              />
              <DialogFooter>
                <Button onClick={createFolder} className="rounded-sm" data-testid="create-folder-confirm">{t("Criar pasta")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-muted-foreground text-sm">{t("Carregando...")}</div>
        ) : visible.length === 0 ? (
          <div className="border border-dashed border-border rounded-sm py-20 text-center" data-testid="empty-state">
            <Layers className="h-10 w-10 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">
              {activeFolder === "all" ? t("Nenhum overlay ainda. Crie o primeiro!") : t("Nenhum overlay nesta pasta.")}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {visible.map((o) => (
              <div
                key={o.id}
                className="group border border-border/60 bg-card rounded-sm overflow-hidden hover:border-primary/50 transition-colors duration-200"
                data-testid={`overlay-card-${o.id}`}
              >
                <div
                  className="checker-bg aspect-video cursor-pointer flex items-center justify-center"
                  onClick={() => navigate(`/studio/${o.id}`)}
                >
                  <Layers className="h-8 w-8 text-white/20" />
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="font-display font-bold truncate">{o.nome}</h3>
                      <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {t("{n} elementos", { n: o.element_count })}
                        {o.folder_id && folderName_(o.folder_id) && (
                          <span className="ml-1 inline-flex items-center gap-1 text-primary/80">
                            <FolderOpen className="h-3 w-3" /> {folderName_(o.folder_id)}
                          </span>
                        )}
                      </p>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className="text-muted-foreground hover:text-foreground transition-colors p-1"
                          data-testid={`overlay-menu-${o.id}`}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onClick={() => duplicate(o.id)} data-testid={`duplicate-overlay-${o.id}`}>
                          <Copy className="h-4 w-4 mr-2" /> {t("Duplicar")}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel className="text-[10px] text-muted-foreground flex items-center gap-1">
                          <FolderInput className="h-3 w-3" /> {t("Mover para")}
                        </DropdownMenuLabel>
                        {o.folder_id && (
                          <DropdownMenuItem onClick={() => moveToFolder(o, null)} data-testid={`move-none-${o.id}`}>
                            <Folder className="h-4 w-4 mr-2" /> {t("Sem pasta")}
                          </DropdownMenuItem>
                        )}
                        {folders.filter((f) => f.id !== o.folder_id).map((f) => (
                          <DropdownMenuItem key={f.id} onClick={() => moveToFolder(o, f.id)} data-testid={`move-${f.id}-${o.id}`}>
                            <FolderOpen className="h-4 w-4 mr-2" /> {f.nome}
                          </DropdownMenuItem>
                        ))}
                        {folders.length === 0 && (
                          <DropdownMenuItem disabled className="text-xs">{t("Nenhuma pasta criada")}</DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <DropdownMenuItem
                              onSelect={(e) => e.preventDefault()}
                              className="text-destructive focus:text-destructive"
                              data-testid={`delete-overlay-${o.id}`}
                            >
                              <Trash2 className="h-4 w-4 mr-2" /> {t("Excluir")}
                            </DropdownMenuItem>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>{t("Excluir \"{nome}\"?", { nome: o.nome })}</AlertDialogTitle>
                              <AlertDialogDescription>{t("Esta ação não pode ser desfeita.")}</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>{t("Cancelar")}</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(o.id)} data-testid={`confirm-delete-${o.id}`}>
                                {t("Excluir")}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <Button
                    variant="secondary"
                    className="w-full mt-4 rounded-sm"
                    onClick={() => navigate(`/studio/${o.id}`)}
                    data-testid={`edit-overlay-${o.id}`}
                  >
                    {t("Abrir editor")}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      <YouTubeKeyWizard open={wizardOpen} onOpenChange={setWizardOpen} onSaved={(s) => setYtStatus(s)} />
    </div>
  );
}
